/**
 * Allows user to change member role for join link, and add/remove kits if limited member.
 */

import React, { useState, useCallback, Fragment } from "react";
import { Flex, Box, Text, Notice } from "@thenounproject/lingo-core";

import ModalBody from "../../../../../app/_components/ModalBody";
import ModalHeader from "../../../../../app/_components/ModalHeader";
import ModalFooter from "../../../../../app/_components/ModalFooter";

import useNotifications from "@actions/useNotifications";
import { useSelectSpace } from "@selectors/entities/spaces";
import useShowModal from "@redux/actions/useModals";
import useUpdateSpaceJoinWith from "@redux/actions/spaces/useUpdateSpaceJoinWith";
import usePortals from "@redux/actions/portals/usePortals";
import PortalSelectList from "../../../portals/PortalSelectList";
import { noop } from "lodash";

const NewUserAccessModal: React.FC = () => {
  const space = useSelectSpace();
  const { showNotification } = useNotifications();
  const { dismissModal } = useShowModal();

  const { data: portalsData, isLoading } = usePortals({ spaceId: space.id });
  const { portals = [] } = portalsData || {};

  const {
    joinSettings: { joinWith },
  } = space;

  const [error, setError] = useState(null),
    [selectedPortals, setSelectedPortals] = useState((joinWith.portals || []).map(p => p.uuid));

  const [updateSpaceJoinWith, { isProcessing }] = useUpdateSpaceJoinWith();

  const submit = useCallback(async () => {
    setError(null);
    const { error: responseError } = await updateSpaceJoinWith({
      spaceId: space.id,
      role: "limited_member",
      portals: selectedPortals,
    });
    if (responseError) {
      setError(responseError.message);
    } else {
      dismissModal();
      showNotification({ message: "Changes saved.", level: "info" });
    }
  }, [dismissModal, selectedPortals, showNotification, space.id, updateSpaceJoinWith]);

  const togglePortals = useCallback(
    (uuid: string) => {
      if (selectedPortals.includes(uuid)) {
        setSelectedPortals(selectedPortals.filter(id => id !== uuid));
      } else {
        setSelectedPortals([...selectedPortals, uuid]);
      }
    },
    [selectedPortals]
  );

  function renderForm() {
    return (
      <Flex flexDirection="column" textAlign="left">
        <Box mt="l">
          <Text font="ui.regularBold" mb="s">
            Portal Access
          </Text>
          <PortalSelectList
            portals={portals}
            isLoading={isLoading}
            selectedPortals={selectedPortals}
            allowPortalKits={false}
            selectPortal={togglePortals}
            selectKitRoles={noop}
            selectedKitRoles={null}
          />
        </Box>
        {error ? <Notice textAlign="left" mt="l" noticeStyle="error" message={error} /> : null}
      </Flex>
    );
  }

  return (
    <Fragment>
      <ModalHeader title="New User Access" />
      <ModalBody>
        <Text textAlign="left" mb="l">
          New users that join your space by signing up through a join link, with an approved email
          or with SSO, will be added as members with access to selected portals.
        </Text>
        {renderForm()}
      </ModalBody>

      <ModalFooter
        primary={{
          id: "save-new-user-access-button",
          text: isProcessing ? "Saving..." : "Save",
          onClick: submit,
          disabled: isProcessing,
        }}
      />
    </Fragment>
  );
};

export default NewUserAccessModal;
